import React, { useEffect, useRef, useState } from 'react';
import { IconPlus, IconX } from '@tabler/icons-react';

export interface ChipSelectOption {
  value: string;
  label: string;
  colors?: { bg: string; text: string; dot: string } | null;
}

interface ChipSelectProps {
  options: ChipSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  addLabel?: string;
}

const ChipSelect: React.FC<ChipSelectProps> = ({
  options,
  value,
  onChange,
  addLabel = 'Добавить',
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) { setSearch(''); return; }
    setTimeout(() => searchRef.current?.focus(), 30);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (optValue: string) => {
    onChange(value.includes(optValue)
      ? value.filter(v => v !== optValue)
      : [...value, optValue]);
  };

  const remove = (optValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter(v => v !== optValue));
  };

  const selectedOptions = value
    .map(id => options.find(o => o.value === id))
    .filter((o): o is ChipSelectOption => Boolean(o));

  const visibleOptions = search.trim()
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  const showSearch = options.length > 7;

  return (
    <div className="cs-wrap" ref={wrapRef}>
      <div className="cs-row">
        {selectedOptions.map(opt => {
          const c = opt.colors;
          return (
            <span
              key={opt.value}
              className="cs-chip"
              style={c ? { background: c.bg, color: c.text, borderColor: c.dot + '66' } : undefined}
            >
              <span className="cs-chip-dot" style={{ background: c?.dot ?? 'var(--text3)' }} />
              {opt.label}
              <button
                type="button"
                className="cs-chip-x"
                onMouseDown={e => remove(opt.value, e)}
              >
                <IconX size={10} stroke={2.5} />
              </button>
            </span>
          );
        })}

        <button
          type="button"
          className={`cs-add${open ? ' open' : ''}`}
          onClick={() => setOpen(p => !p)}
        >
          <IconPlus size={13} stroke={2.5} />
          {selectedOptions.length === 0 && <span>{addLabel}</span>}
        </button>
      </div>

      {open && (
        <div className="cs-drop">
          {showSearch && (
            <div className="cs-search-wrap">
              <input
                ref={searchRef}
                className="cs-search"
                placeholder="Поиск..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button type="button" className="cs-search-clear" onMouseDown={e => { e.preventDefault(); setSearch(''); }}>
                  <IconX size={11} stroke={2.5} />
                </button>
              )}
            </div>
          )}
          <div className="cs-options">
            {visibleOptions.map(opt => {
              const c = opt.colors;
              const isSelected = value.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`cs-option${isSelected ? ' selected' : ''}`}
                  style={c ? {
                    background: isSelected ? c.bg : undefined,
                    color: isSelected ? c.text : undefined,
                    borderColor: isSelected ? c.dot + '88' : undefined,
                  } : undefined}
                  onMouseDown={e => { e.preventDefault(); toggle(opt.value); }}
                >
                  <span className="cs-option-dot" style={{ background: c?.dot ?? 'var(--text3)' }} />
                  {opt.label}
                  {isSelected && <IconX size={9} stroke={2.5} className="cs-option-x" />}
                </button>
              );
            })}
            {visibleOptions.length === 0 && (
              <div className="cs-empty">Ничего не найдено</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export { ChipSelect };
